import { Button, Typography } from "@mui/material";

import { formatDate } from "@aclymatepackages/formatters";

import Link from "../../../atoms/mui/Link";

import { useCachedDisplayData } from "../../../../helpers/firebase";
import { isSharedProduct } from "../../../../helpers/components/explorerProductsTab";
import { NAVIGATOR_UPGRADE_PATH } from "../../helpers";

import CollectionScreen, { StatusChip } from "./CollectionScreen";

const SUPPLIERS_BUYER_VIEW_HREF = (relationshipId) =>
  `/explorer/dashboard?view=suppliers&selectedId=${relationshipId}`;

const SUPPLIERS_EMPTY_TEXT = (
  <>
    No suppliers yet. Explorer surfaces suppliers that have shared their carbon
    footprint reports with you from Aclymate Navigator. To actively share your
    report with suppliers or to invite suppliers,{" "}
    <Link href={NAVIGATOR_UPGRADE_PATH} color="secondary">
      upgrade to Aclymate Navigator
    </Link>
    .
  </>
);

const findLatestSurveyEntry = (relationship) => {
  const entries = relationship.surveyData || [];
  if (!entries.length) {
    return null;
  }
  return entries.reduce((latest, entry) => {
    const entryYear = Number(entry.year) || 0;
    const latestYear = Number(latest?.year) || 0;
    return entryYear >= latestYear ? entry : latest;
  }, entries[0]);
};

const formatLastActivity = (relationship) => {
  const latest = findLatestSurveyEntry(relationship);
  const candidate =
    latest?.dateCompleted ||
    latest?.dateRequested ||
    relationship.survey?.dateCompleted ||
    relationship.survey?.dateInvited;
  if (!candidate) {
    return "—";
  }
  return formatDate(candidate);
};

const countSharedProducts = (relationship) =>
  (relationship.sharedProducts || []).filter(isSharedProduct).length;

const formatSharedProducts = (count) =>
  count > 0 ? `${count} product${count === 1 ? "" : "s"}` : "—";

export const buildSupplierRow = (relationship) => {
  const latest = findLatestSurveyEntry(relationship);
  const status = latest?.status || relationship.survey?.status || null;
  const sharedProductsCount = countSharedProducts(relationship);

  const baseRow = {
    id: relationship.id,
    name: relationship.vendorName || "Unnamed supplier",
    category: relationship.vendorData?.scopeThreeCategory || "Uncategorized",
    products: formatSharedProducts(sharedProductsCount)
  };

  if (status === "accepted" || status === "confirmed") {
    return {
      ...baseRow,
      statusLabel: "Confirmed",
      statusTone: "success",
      shared: "Shared",
      updated: formatLastActivity(relationship),
      action: "View"
    };
  }

  if (
    status === "pending" ||
    status === "unconfirmed" ||
    status === "invited" ||
    status === "requested"
  ) {
    return {
      ...baseRow,
      statusLabel: "Awaiting response",
      statusTone: "info",
      shared: "Awaiting",
      updated: formatLastActivity(relationship),
      action: "Resend"
    };
  }

  if (sharedProductsCount > 0) {
    return {
      ...baseRow,
      statusLabel: "Shared products",
      statusTone: "pending",
      shared: "—",
      updated: formatLastActivity(relationship),
      action: "View"
    };
  }

  return {
    ...baseRow,
    statusLabel: "Not invited",
    statusTone: "neutral",
    shared: "—",
    updated: "—",
    action: "Invite"
  };
};

const countByTone = (rows, tone) =>
  rows.filter((row) => row.statusTone === tone).length;

const isAcceptedReport = (entry) => entry?.status === "accepted";

const isAwaitingReport = (entry) => entry?.status === "pending";

const countReports = (relationships, matcher) =>
  (relationships || []).reduce(
    (sum, { surveyData }) => sum + (surveyData || []).filter(matcher).length,
    0
  );

const COLUMNS = [
  { key: "name", label: "Supplier" },
  { key: "category", label: "Category" },
  {
    key: "status",
    label: "Status",
    render: (row) => <StatusChip label={row.statusLabel} tone={row.statusTone} />
  },
  { key: "shared", label: "Emissions data" },
  { key: "products", label: "Products shared" },
  { key: "updated", label: "Last activity" },
  {
    key: "action",
    label: "",
    align: "right",
    render: (row) => (
      <Link href={SUPPLIERS_BUYER_VIEW_HREF(row.id)}>
        <Button color="primary" size="small">
          <Typography variant="caption" color="primary">
            {row.action}
          </Typography>
        </Button>
      </Link>
    )
  }
];

const SuppliersTab = () => {
  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");
  const rows = (vendorRelationships || []).map(buildSupplierRow);
  const confirmed = countByTone(rows, "success");
  const awaiting = countByTone(rows, "info");
  const sharingProducts = countByTone(rows, "pending");
  const notInvited = countByTone(rows, "neutral");

  // Assumption for Bill's suppliers stat cards: "accepted" == buyer has
  // viewed/accepted the supplier's shared report (surveyData.status="accepted"),
  // "awaiting acceptance" == supplier has shared, buyer hasn't accepted yet
  // (surveyData.status="pending"). These two count individual year-reports
  // across relationships, not deduped suppliers. The "Suppliers Sharing
  // Products" card below instead counts distinct suppliers (deduped) that have
  // shared any product. Revisit if a dedicated "viewed" field is added to
  // sharedReports/surveyData.
  const acceptedReports = countReports(vendorRelationships, isAcceptedReport);
  const awaitingReports = countReports(vendorRelationships, isAwaitingReport);
  const totalReports = acceptedReports + awaitingReports;
  const suppliersSharingProducts = (vendorRelationships || []).filter(
    (relationship) => countSharedProducts(relationship) > 0
  ).length;

  const stats = [
    { label: "Total Suppliers", value: totalReports, tone: "total" },
    {
      label: "Accepted Supplier Reports",
      value: acceptedReports,
      tone: "success"
    },
    {
      label: "Reports Awaiting Acceptance",
      value: awaitingReports,
      tone: "warning"
    },
    {
      label: "Suppliers Sharing Products",
      value: suppliersSharingProducts,
      tone: "success"
    }
  ];

  const statusSegments = [
    { label: "Confirmed", value: confirmed, tone: "success" },
    { label: "Awaiting response", value: awaiting, tone: "info" },
    { label: "Shared products", value: sharingProducts, tone: "pending" },
    { label: "Not invited", value: notInvited, tone: "neutral" }
  ];

  return (
    <CollectionScreen
      heading="My Suppliers"
      subtitle="See who's shared emissions data and who still owes you a response."
      stats={stats}
      statusTitle="Supplier response status"
      statusSegments={statusSegments}
      tableTitle="Suppliers"
      columns={COLUMNS}
      rows={rows}
      emptyText={SUPPLIERS_EMPTY_TEXT}
    />
  );
};

export default SuppliersTab;
